/*
 * @Author: Auto Generated
 * @FilePath: /mall-fe/src/comps/ProcureEnumProvider.tsx
 * @Description: 采购枚举字典 Provider
 *
 * 在客户端获取采购枚举字典数据，并通过 Context 提供给子组件
 * 用于订单、支付、结算等业务场景的枚举值映射
 */

import { ReactNode } from 'react';
import { ProcureEnumDictionaryContext } from 'libs/contexts';
import { useProcureEnumQuery } from 'hooks/useProcureEnumQuery';

export interface ProcureEnumProviderProps {
  children: ReactNode;
  /**
   * 初始数据（可选）
   * 用于服务端渲染场景，如果提供则使用初始数据，避免客户端重新请求
   */
  initialData?: Record<string, any>;
}

/**
 * 采购枚举字典 Provider 组件
 *
 * 功能：
 * 1. 使用 React Query 在客户端获取采购枚举字典
 * 2. 通过 ProcureEnumDictionaryContext 提供数据给子组件
 * 3. 自动处理 loading 和 error 状态
 * 4. 支持初始数据，避免重复请求
 *
 * @example
 * ```tsx
 * // 在 _app.tsx 中使用
 * <ProcureEnumProvider>
 *   <YourApp />
 * </ProcureEnumProvider>
 *
 * // 在子组件中使用
 * function OrderStatus({ status }) {
 *   const enumDict = useContext(ProcureEnumDictionaryContext);
 *   const statusText = enumDict?.orderStatus?.[status] || '未知状态';
 *   return <span>{statusText}</span>;
 * }
 * ```
 */
export default function ProcureEnumProvider({ children, initialData }: ProcureEnumProviderProps) {
  // 使用 React Query 获取采购枚举字典
  const {
    data: procureEnum,
    isLoading,
    isError,
    error,
  } = useProcureEnumQuery({
    initialData,
  });

  // 错误处理：如果加载失败，使用空对象避免组件崩溃
  if (isError) {
    console.error('加载采购枚举字典失败:', error);
  }

  // 提供数据给子组件
  // 注意：
  // 1. loading 期间提供空对象，避免子组件因为 undefined 崩溃
  // 2. error 期间也提供空对象，让应用继续运行
  // 3. 成功后提供实际数据
  const enumData = isLoading || isError ? initialData || {} : procureEnum || {};

  return (
    <ProcureEnumDictionaryContext.Provider value={enumData}>
      {children}
    </ProcureEnumDictionaryContext.Provider>
  );
}
